import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const StudentFees = ({ onNavigate, userInfo }) => {

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [selectedSemester, setSelectedSemester] = useState('Current Semester');
  const [uploadFile, setUploadFile] = useState(null);

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);

  const [feesData, setFeesData] = useState({
    hasPendingFees: false,
    totalPendingAmount: 0,
    semesters: [],
    currentSemester: null
  });

  const [availableSemesters, setAvailableSemesters] = useState([]);

  // New States for Payment Tabs
  const [activeTab, setActiveTab] = useState('semester');

  // Demo Payment States
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('upi'); // 'upi', 'qr', 'card'
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [upiId, setUpiId] = useState('');

  const [examFeesData] = useState([
    {
      id: 1,
      examName: 'End Semester Regular Exam',
      semester: 'Semester 3',
      amount: 1200,
      dueDate: '2025-06-15',
      status: 'Pending'
    },
    {
      id: 2,
      examName: 'Mid Semester Exam',
      semester: 'Semester 3',
      amount: 500,
      dueDate: '2025-04-10',
      status: 'Paid',
      paidDate: '2025-04-05'
    }
  ]);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch Fee Data from API
  useEffect(() => {
    const fetchFeeData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting API call to fetch fee data...');

        // Get token from storage
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          console.log('❌ No token found');
          setError('Authentication token not found. Please login again.');
          setLoading(false);
          return;
        }

        // Fetch fee data
        const feeResponse = await fetch(`${API_LINK}/api/student/fees`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Fee Response status:', feeResponse.status);

        if (!feeResponse.ok) {
          if (feeResponse.status === 401) {
            console.log('🔐 Authentication failed');
            setError('Session expired. Please login again.');
            window.location.href = '/';
            return;
          } else if (feeResponse.status === 404) {
            console.log('🔍 Fee data not found');
            setError('Fee information not available.');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${feeResponse.status}`);
        }

        const feeResult = await feeResponse.json();
        console.log('📦 Fee API Response:', feeResult);

        if (true) { // Force success for demo
          // Mock Data to override API
          const mockData = {
            hasPendingFees: true,
            totalPendingAmount: 30000,
            currentSemester: {
              id: 1,
              semester: "Semester 3",
              totalFee: 30000,
              paidAmount: 0,
              dueAmount: 30000,
              dueDate: "2025-06-15",
              breakdown: {
                tuitionFee: 20000,
                developmentFee: 5000,
                libraryFee: 3000,
                examFee: 2000
              }
            },
            semesters: [
              {
                id: 1,
                semester: "Semester 3",
                totalFee: 30000,
                paidAmount: 0,
                dueAmount: 30000,
                dueDate: "2025-06-15"
              },
              {
                id: 2,
                semester: "Semester 2",
                totalFee: 80000,
                paidAmount: 80000,
                dueAmount: 0,
                dueDate: "2024-12-15"
              }
            ]
          };

          setFeesData(mockData);

          // Build available semesters list
          const semesters = ['Current Semester'];
          if (mockData.semesters && mockData.semesters.length > 0) {
            mockData.semesters.forEach(sem => {
              if (!semesters.includes(sem.semester)) {
                semesters.push(sem.semester);
              }
            });
          }
          setAvailableSemesters(semesters);

          console.log('✅ Fee data updated with MOCK data');
        } else {
          console.log('❌ API returned error:', feeResult.message);
          setError(feeResult.message || 'Failed to fetch fee data');
        }

      } catch (error) {
        console.error('💥 Error fetching fee data:', error);
        setError(`Failed to load fee data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to ensure component is mounted
    const timer = setTimeout(fetchFeeData, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);



  // Get current semester data
  const getCurrentSemesterData = () => {
    if (selectedSemester === 'Current Semester') {
      // Return the latest semester with pending fees, or the most recent one
      const pendingSemester = feesData.semesters?.find(sem => parseFloat(sem.dueAmount) > 0);
      return pendingSemester || feesData.semesters?.[0] || null;
    } else {
      return feesData.semesters?.find(sem => sem.semester === selectedSemester) || null;
    }
  };

  const currentSemesterData = getCurrentSemesterData();

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setError('Please select an image or PDF file');
      return;
    }

    setUploadFile(file);
    setError(null);
    setUploadSuccess(null);

    // Auto-upload the file
    try {
      setUploading(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setError('Authentication required');
        return;
      }

      if (!currentSemesterData) {
        setError('No semester data available for upload');
        return;
      }

      const formData = new FormData();
      formData.append('paymentProof', file);
      formData.append('semesterId', currentSemesterData.id || '');
      formData.append('semester', currentSemesterData.semester || selectedSemester);
      formData.append('amount', currentSemesterData.dueAmount || '0');

      const response = await fetch(`${API_LINK}/api/student/fees/upload-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setUploadSuccess('Payment proof uploaded successfully! Admin will verify and process your payment.');
        setUploadFile(null);
        // Reset file input
        event.target.value = '';
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to upload payment proof');
      }
    } catch (uploadError) {
      console.error('Upload error:', uploadError);
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Handle Demo Payment Simulation
  const handleProcessPayment = () => {
    if (paymentMethod === 'upi' && !upiId) {
      alert('Please enter a valid UPI ID');
      return;
    }
    if (paymentMethod === 'card' && !cardNumber) {
      alert('Please enter card details');
      return;
    }

    setProcessingPayment(true);

    // Simulate API call
    setTimeout(() => {
      setProcessingPayment(false);
      setPaymentSuccess(true);
      setTimeout(() => {
        setShowPaymentModal(false);
        setPaymentSuccess(false);
        setCardNumber('');
        setUpiId('');
        setActiveTab('semester'); // Redirect back to main tab
        alert('Payment Successful! Transaction ID: TXN' + Math.floor(Math.random() * 10000000));
      }, 2000);
    }, 2000);
  };



  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Fee Information...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Fee Information
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {error}
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Try Again
            </button>

            <button
              onClick={() => window.location.href = '/'}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="student"
      onNavigate={onNavigate}
      activePage="student-fees"
      pageTitle="Fee Management "
    >
      {/* Fee Management Header */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem',
        overflow: 'hidden'
      }}>
        <div style={{
          background: '#238548',
          color: '#ffffff',
          padding: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <h2 style={{ margin: 0, fontSize: '1.4rem' }}>₹ Fee Management (coming soon...) </h2>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <label style={{ color: '#ffffff', fontSize: '1rem' }}>Select Semester:</label>
            <select
              value={selectedSemester}
              onChange={(e) => setSelectedSemester(e.target.value)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#ffffff',
                color: '#333',
                fontSize: '1rem',
                cursor: 'pointer',
                minWidth: '150px'
              }}
            >
              {availableSemesters.map((semester, index) => (
                <option key={index} value={semester}>{semester}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Status Alert */}
        <div style={{
          background: currentSemesterData ? '#238548' : '#f39c12',
          color: '#ffffff',
          padding: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '1rem'
        }}>
          <span>
            {currentSemesterData
              ? `${currentSemesterData.semester || selectedSemester} fee information`
              : 'No data available for this semester'
            }
          </span>
          <span style={{
            fontSize: '0.9rem',
            opacity: 0.8
          }}>
            {currentSemesterData
              ? (parseFloat(currentSemesterData.dueAmount) > 0 ? 'Pending' : 'Paid')
              : 'Unknown'}
          </span>
        </div>
      </div>

      {/* Error/Success Messages */}
      {(error || uploadSuccess) && (
        <div style={{
          background: uploadSuccess ? '#d4edda' : '#f8d7da',
          border: `1px solid ${uploadSuccess ? '#c3e6cb' : '#f5c6cb'}`,
          color: uploadSuccess ? '#155724' : '#721c24',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.2rem' }}>
            {uploadSuccess ? '✅' : '❌'}
          </span>
          {uploadSuccess || error}
          {(error || uploadSuccess) && (
            <button
              onClick={() => {
                setError(null);
                setUploadSuccess(null);
              }}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: '1.2rem'
              }}
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        borderBottom: '2px solid #e0e0e0',
        paddingBottom: '1px'
      }}>
        <button
          onClick={() => setActiveTab('semester')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'semester' ? '3px solid #238548' : '3px solid transparent',
            color: activeTab === 'semester' ? '#238548' : '#666',
            fontWeight: activeTab === 'semester' ? '600' : '500',
            fontSize: '1rem',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            marginBottom: '-2px'
          }}
        >
          Semester Fees
        </button>
        <button
          onClick={() => setActiveTab('exam')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'exam' ? '3px solid #238548' : '3px solid transparent',
            color: activeTab === 'exam' ? '#238548' : '#666',
            fontWeight: activeTab === 'exam' ? '600' : '500',
            fontSize: '1rem',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            marginBottom: '-2px'
          }}
        >
          Exam Form Payment
        </button>
      </div>

      {/* Conditional Rendering based on Active Tab */}
      {activeTab === 'semester' ? (
        /* SEMESTER FEES VIEW */
        <>
          {/* Fee Summary */}
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            padding: '2rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '2rem'
            }}>
              {/* Left Column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingBottom: '0.5rem',
                  borderBottom: '1px solid #ecf0f1'
                }}>
                  <span style={{ color: '#666', fontSize: '1rem' }}>Total Fee Amount</span>
                  <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#333' }}>
                    {formatCurrency(currentSemesterData?.totalFee)}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingBottom: '0.5rem',
                  borderBottom: '1px solid #ecf0f1'
                }}>
                  <span style={{ color: '#666', fontSize: '1rem' }}>Paid Amount</span>
                  <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#28a745' }}>
                    {formatCurrency(currentSemesterData?.paidAmount)}
                  </span>
                </div>
              </div>

              {/* Right Column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingBottom: '0.5rem',
                  borderBottom: '1px solid #ecf0f1'
                }}>
                  <span style={{ color: '#666', fontSize: '1rem' }}>Due Amount</span>
                  <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#dc3545' }}>
                    {formatCurrency(currentSemesterData?.dueAmount)}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingBottom: '0.5rem',
                  borderBottom: '1px solid #ecf0f1'
                }}>
                  <span style={{ color: '#666', fontSize: '1rem' }}>Due Date</span>
                  <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#333' }}>
                    {formatDate(currentSemesterData?.dueDate)}
                  </span>
                </div>

                {parseFloat(currentSemesterData?.dueAmount) > 0 && (
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    style={{
                      background: '#238548',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginTop: '0.5rem',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#1e7040'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#238548'}
                  >
                    Pay Now
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Upload Payment Proof */}
          {currentSemesterData && parseFloat(currentSemesterData.dueAmount) > 0 && (
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              marginBottom: '1.5rem',
              overflow: 'hidden'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1.5rem',
                borderBottom: '1px solid #ecf0f1'
              }}>
                <span style={{ fontSize: '1.5rem', color: '#238548' }}>📤</span>
                <h3 style={{ margin: 0, color: '#238548', fontSize: '1.1rem' }}>Upload Payment Proof</h3>
              </div>
              <div style={{ padding: '1.5rem' }}>
                <p style={{
                  margin: '0 0 1rem 0',
                  color: '#666',
                  fontSize: '0.95rem'
                }}>
                  Upload screenshot of your payment or offline receipt. Admin will verify and generate an official receipt.
                </p>
                <div style={{
                  border: '2px dashed #97d6ac',
                  borderRadius: '8px',
                  padding: '2rem',
                  textAlign: 'center',
                  backgroundColor: '#f8fffe'
                }}>
                  <input
                    type="file"
                    id="payment-proof"
                    accept="image/*,.pdf"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    style={{ display: 'none' }}
                  />
                  <label
                    htmlFor="payment-proof"
                    style={{
                      display: 'inline-block',
                      background: uploading ? '#ccc' : '#238548',
                      color: '#ffffff',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!uploading) {
                        e.target.style.backgroundColor = '#1e7040';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!uploading) {
                        e.target.style.backgroundColor = '#238548';
                      }
                    }}
                  >
                    {uploading ? '⏳ Uploading...' : 'Choose File'}
                  </label>
                  {uploadFile && (
                    <p style={{
                      margin: '1rem 0 0 0',
                      color: '#238548',
                      fontSize: '0.9rem'
                    }}>
                      Selected: {uploadFile.name}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Fee Structure and Payment History */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '1.5rem'
          }}>
            {/* Fee Structure */}
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              <div style={{
                background: '#238548',
                color: '#ffffff',
                padding: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <span style={{ fontSize: '1.5rem' }}>📋</span>
                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Fee Structure</h3>
              </div>
              <div style={{ padding: '1.5rem' }}>
                {currentSemesterData?.breakdown ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {Object.entries(currentSemesterData.breakdown).map(([key, value]) => (
                      <div key={key} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: '0.5rem',
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <span style={{ color: '#666', textTransform: 'capitalize' }}>
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </span>
                        <span style={{ fontWeight: '500' }}>{formatCurrency(value)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    color: '#666',
                    padding: '1rem'
                  }}>
                    <p style={{ margin: 0, fontSize: '1rem' }}>
                      No fee structure available for this semester.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Semesters */}
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              <div style={{
                background: '#238548',
                color: '#ffffff',
                padding: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <span style={{ fontSize: '1.5rem' }}>🔄</span>
                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>All Semesters</h3>
              </div>
              <div style={{ padding: '1.5rem' }}>
                {feesData.semesters?.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {feesData.semesters.slice(0, 5).map((semester, index) => (
                      <div key={index} style={{
                        padding: '1rem',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        border: parseFloat(semester.dueAmount) > 0 ? '2px solid #ffc107' : '1px solid #e9ecef'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.5rem'
                        }}>
                          <span style={{ fontWeight: '600', color: '#333' }}>
                            {semester.semester}
                          </span>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '15px',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                            backgroundColor: parseFloat(semester.dueAmount) > 0 ? '#fff3cd' : '#d1e7dd',
                            color: parseFloat(semester.dueAmount) > 0 ? '#856404' : '#0f5132'
                          }}>
                            {parseFloat(semester.dueAmount) > 0 ? 'Pending' : 'Paid'}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                          Due: {formatCurrency(semester.dueAmount)} |
                          Paid: {formatCurrency(semester.paidAmount)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    color: '#666',
                    padding: '1rem'
                  }}>
                    <p style={{ margin: 0, fontSize: '1rem' }}>
                      No semester data available.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        /* EXAM FORM PAYMENT VIEW */
        <div style={{
          display: 'grid',
          gap: '1.5rem'
        }}>
          {examFeesData.length > 0 ? (
            examFeesData.map((exam, index) => (
              <div key={index} style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                padding: '1.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  marginBottom: isMobile ? '1rem' : '0'
                }}>
                  <h3 style={{ margin: 0, color: '#333', fontSize: '1.2rem' }}>{exam.examName}</h3>
                  <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem', color: '#666', flexWrap: 'wrap' }}>
                    <span>Semester: {exam.semester}</span>
                    <span>•</span>
                    <span>Due Date: {formatDate(exam.dueDate)}</span>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.5rem',
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: isMobile ? 'space-between' : 'flex-end'
                }}>
                  <div style={{
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: '#238548'
                  }}>
                    {formatCurrency(exam.amount)}
                  </div>

                  {exam.status === 'Pending' ? (
                    <button
                      style={{
                        backgroundColor: '#238548',
                        color: '#ffffff',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#1e7040'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#238548'}
                    >
                      <span>Pay Now</span>
                      <span>→</span>
                    </button>
                  ) : (
                    <div style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      backgroundColor: '#d1e7dd',
                      color: '#0f5132',
                      fontWeight: '500',
                      fontSize: '0.9rem'
                    }}>
                      Paid on {formatDate(exam.paidDate)}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              padding: '3rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📝</div>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>No Exam Forms Available</h3>
              <p style={{ margin: 0, color: '#666' }}>There are no upcoming exams requiring payment at this time.</p>
            </div>
          )}
        </div>
      )}


      {/* Payment Modal */}
      {showPaymentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Modal Header */}
            <div style={{
              background: '#238548',
              color: '#ffffff',
              padding: '1.5rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0 }}>Payment Gateway</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ffffff',
                  fontSize: '1.5rem',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '2rem' }}>
              <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <p style={{ margin: 0, color: '#666' }}>Paying Amount</p>
                <h2 style={{ margin: '0.25rem 0 0 0', color: '#333' }}>
                  {formatCurrency(30000)}
                </h2>
              </div>

              {/* Payment Methods Tabs */}
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                marginBottom: '2rem',
                background: '#f4f7fa',
                padding: '0.5rem',
                borderRadius: '8px'
              }}>
                <button
                  onClick={() => setPaymentMethod('upi')}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: 'none',
                    borderRadius: '6px',
                    background: paymentMethod === 'upi' ? '#ffffff' : 'transparent',
                    color: paymentMethod === 'upi' ? '#238548' : '#666',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: paymentMethod === 'upi' ? '0 2px 5px rgba(0,0,0,0.05)' : 'none',
                    transition: 'all 0.3s ease'
                  }}
                >
                  UPI
                </button>
                <button
                  onClick={() => setPaymentMethod('qr')}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: 'none',
                    borderRadius: '6px',
                    background: paymentMethod === 'qr' ? '#ffffff' : 'transparent',
                    color: paymentMethod === 'qr' ? '#238548' : '#666',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: paymentMethod === 'qr' ? '0 2px 5px rgba(0,0,0,0.05)' : 'none',
                    transition: 'all 0.3s ease'
                  }}
                >
                  QR Code
                </button>
                <button
                  onClick={() => setPaymentMethod('card')}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: 'none',
                    borderRadius: '6px',
                    background: paymentMethod === 'card' ? '#ffffff' : 'transparent',
                    color: paymentMethod === 'card' ? '#238548' : '#666',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: paymentMethod === 'card' ? '0 2px 5px rgba(0,0,0,0.05)' : 'none',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Card
                </button>
              </div>

              {/* Payment Details Form */}
              <div style={{ minHeight: '150px' }}>
                {processingPayment ? (
                  <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      border: '4px solid #e3f2fd',
                      borderTop: '4px solid #238548',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 1rem'
                    }}></div>
                    <p style={{ color: '#238548', fontWeight: '500' }}>Processing Payment...</p>
                    <p style={{ color: '#666', fontSize: '0.9rem' }}>Please do not close this window</p>
                  </div>
                ) : paymentSuccess ? (
                  <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
                    <h3 style={{ color: '#238548', margin: '0 0 0.5rem 0' }}>Payment Successful!</h3>
                    <p style={{ color: '#666' }}>Redirecting...</p>
                  </div>
                ) : (
                  <>
                    {paymentMethod === 'upi' && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Enter UPI ID</label>
                        <input
                          type="text"
                          placeholder="username@upi"
                          value={upiId}
                          onChange={(e) => setUpiId(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '1rem',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            outline: 'none'
                          }}
                        />
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#666' }}>
                          Example: 9876543210@ybl, name@oksbi
                        </p>
                      </div>
                    )}

                    {paymentMethod === 'qr' && (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          width: '200px',
                          height: '200px',
                          margin: '0 auto 1rem',
                          background: '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px dashed #ddd',
                          borderRadius: '12px'
                        }}>
                          <span style={{ fontSize: '1.2rem', color: '#666' }}>[ QR Code Placeholder ]</span>
                        </div>
                        <p style={{ margin: 0, color: '#666' }}>Scan this QR code with any UPI app</p>
                      </div>
                    )}

                    {paymentMethod === 'card' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Card Number</label>
                          <input
                            type="text"
                            placeholder="0000 0000 0000 0000"
                            value={cardNumber}
                            onChange={(e) => setCardNumber(e.target.value)}
                            maxLength={19}
                            style={{
                              width: '100%',
                              padding: '1rem',
                              border: '1px solid #ddd',
                              borderRadius: '8px',
                              fontSize: '1rem',
                              outline: 'none'
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '1rem' }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Expiry</label>
                            <input
                              type="text"
                              placeholder="MM/YY"
                              style={{
                                width: '100%',
                                padding: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                outline: 'none'
                              }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>CVV</label>
                            <input
                              type="password"
                              placeholder="123"
                              maxLength={3}
                              style={{
                                width: '100%',
                                padding: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                outline: 'none'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={handleProcessPayment}
                      style={{
                        width: '100%',
                        background: '#238548',
                        color: '#ffffff',
                        border: 'none',
                        padding: '1rem',
                        borderRadius: '8px',
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        marginTop: '2rem',
                        transition: 'background-color 0.3s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#1e7040'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#238548'}
                    >
                      Pay {formatCurrency(30000)}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default StudentFees;